import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import toast from "react-hot-toast";
import { ArrowLeft, ChevronLeft, ChevronRight, Hand, Plus, Trash2, X } from "lucide-react";
import { LeafletMap, type MapDepot, type MapStop } from "@/components/maps/LeafletMap";
import {
  appendRouteStop,
  cancelRoute,
  claimRoute,
  deleteRoute,
  getRoute,
  type LeadStatus,
  listMappableLeads,
  type MappableLead,
  reassignRoute,
  removeRouteStop,
  type RouteStatus,
  type RouteStopStatus,
  updateRouteStopStatus,
} from "@/services/routes";
import { listTeamMembers } from "@/services/team";
import { listDepots } from "@/services/depots";
import { setStopAppointment } from "@/services/appointments";
import { formatMiles, formatDuration } from "@/lib/distance";
import { optimizeDriveRoute } from "@/services/directions";
import { buildAppleMapsRouteUrl, buildGoogleMapsRouteUrl, buildRouteAddressList, preferAppleMaps } from "@/lib/mapsLinks";
import { formatPhoneDisplay, toMailtoHref, toTelHref } from "@/lib/contactLinks";
import { decodePolyline } from "@/lib/polyline";
import { useAuthStore } from "@/store/auth";
import { useIsAdmin, useIsFullAdmin } from "@/hooks/useCurrentUser";
import { nextSelectionFromClick, pageSelectionState, togglePageSelection } from "@/lib/selection";

const DIRECTIONS_WAYPOINT_LIMIT = 25;

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

const STATUS_STYLES: Record<RouteStatus, string> = {
  draft: "bg-slate-100 text-slate-700",
  published: "bg-amber-50 text-amber-700",
  in_progress: "bg-sky-50 text-sky-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

const STOP_STATUS_STYLES: Record<RouteStopStatus, string> = {
  pending: "bg-slate-100 text-slate-700",
  arrived: "bg-sky-50 text-sky-700",
  completed: "bg-emerald-50 text-emerald-700",
  skipped: "bg-amber-50 text-amber-700",
  no_answer: "bg-rose-50 text-rose-700",
};

const LEAD_STATUS_STYLES: Record<LeadStatus, string> = {
  new: "bg-slate-100 text-slate-700",
  no_answer: "bg-rose-50 text-rose-700",
  scheduled: "bg-sky-50 text-sky-700",
  follow_up: "bg-amber-50 text-amber-700",
  closed: "bg-emerald-50 text-emerald-700",
  do_not_solicit: "bg-zinc-200 text-zinc-700",
};

function formatDob(isoDate: string | null | undefined): string {
  if (!isoDate) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return isoDate;
  return `${m[2]}/${m[3]}/${m[1]}`;
}

// ---------- Resume-route position (per-device) ----------
// Mobile browsers evict backgrounded tabs; when the driver returns, the page
// fully remounts. Persisting the active stop per-route lets them pick up
// exactly where they left off. localStorage failures (private mode, quota)
// are swallowed — worst case we fall back to the first unworked stop.
function activeStopStorageKey(routeId: string): string {
  return `rms.route.${routeId}.activeStop`;
}

function readSavedActiveStop(routeId: string): string | null {
  if (!routeId) return null;
  try {
    return localStorage.getItem(activeStopStorageKey(routeId));
  } catch {
    return null;
  }
}

function writeSavedActiveStop(routeId: string, stopId: string): void {
  try {
    localStorage.setItem(activeStopStorageKey(routeId), stopId);
  } catch {
    // ignore — resume falls back to first unworked stop
  }
}

function clearSavedActiveStop(routeId: string): void {
  try {
    localStorage.removeItem(activeStopStorageKey(routeId));
  } catch {
    // ignore
  }
}

function humanizeCustomFieldKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCustomFieldValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function RouteDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  // Route management is part of the elevated bucket — admin AND route_manager
  // can claim/cancel routes, manage stops, see admin-side actions.
  const isAdmin = useIsAdmin();
  // Hard delete destroys visit history — admin_user only (matches the
  // routes_delete RLS policy patched in migration 0033). route_manager
  // still gets Cancel, which preserves history.
  const isFullAdmin = useIsFullAdmin();

  const routeQuery = useQuery({ queryKey: ["routes", "detail", id], queryFn: () => getRoute(id), enabled: !!id });
  const depotsQuery = useQuery({ queryKey: ["depots"], queryFn: listDepots });
  // Members for the reassign dropdown — elevated roles only.
  const membersQuery = useQuery({
    queryKey: ["team", "members"],
    queryFn: listTeamMembers,
    enabled: isAdmin,
    staleTime: 5 * 60_000,
  });
  const [activeStopId, setActiveStopId] = useState<string | null>(null);
  const [noteDraftByStop, setNoteDraftByStop] = useState<Record<string, string>>({});
  const [selectedStopIds, setSelectedStopIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(25);
  // Phase 2D: editing stops post-save. The picker is collapsed by default so
  // it doesn't clutter the routing UI for drivers who only need to run the
  // route — admins/owners click "+ Add stop" to reveal it.
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [addPickerQuery, setAddPickerQuery] = useState("");
  // Appointment booking panel — collapsed until "Set appointment" is tapped.
  const [apptPickerOpen, setApptPickerOpen] = useState(false);
  const [apptWhen, setApptWhen] = useState("");
  const navigatorRef = useRef<HTMLElement | null>(null);
  const lastClickedStopIdRef = useRef<string | null>(null);
  const swipeStartX = useRef<number | null>(null);

  const deleteMut = useMutation({
    mutationFn: () => deleteRoute(id),
    onSuccess: () => {
      toast.success("Route deleted");
      qc.invalidateQueries({ queryKey: ["routes"] });
      // Deleted route releases its leads back into the mappable pool.
      qc.invalidateQueries({ queryKey: ["leads"] });
      navigate("/routes");
    },
    onError: (e: Error) => {
      // DB trigger (migration 0035) blocks deleting worked routes.
      if (e.message.includes("route_has_history")) {
        toast.error("This route has recorded visit outcomes — cancel it instead so the history is preserved.");
        return;
      }
      toast.error(e.message);
    },
  });

  const claimMut = useMutation({
    mutationFn: () => claimRoute(id),
    onSuccess: () => {
      toast.success("Route claimed");
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["routes", "detail", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reassignMut = useMutation({
    mutationFn: (assignee: string | "pool") => reassignRoute(id, assignee),
    onSuccess: (_d, assignee) => {
      toast.success(assignee === "pool" ? "Route sent to the team pool" : "Route reassigned");
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["routes", "detail", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelRoute(id),
    onSuccess: () => {
      toast.success("Route cancelled — its leads are back in the pool");
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["routes", "detail", id] });
      // Cancelled routes release their stops, so the mappable lead pool changes.
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const stopMut = useMutation({
    mutationFn: ({
      stopId,
      status,
      leadStatus,
      eventType,
      notes,
    }: {
      stopId: string;
      status: RouteStopStatus;
      leadStatus: LeadStatus;
      eventType: "arrived" | "completed" | "skipped" | "no_answer" | "sale" | "follow_up" | "note";
      notes?: string;
    }) => updateRouteStopStatus(stopId, status, { leadStatus, eventType, notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["routes", "detail", id] });
      // Stop outcome also writes lead.status — keep Leads list + planner + map fresh.
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const apptMut = useMutation({
    mutationFn: ({ stopId, scheduledAt, notes }: { stopId: string; scheduledAt: string; notes?: string }) =>
      setStopAppointment(stopId, scheduledAt, notes),
    onSuccess: (_id, vars) => {
      toast.success(`Appointment booked — ${format(new Date(vars.scheduledAt), "EEE, MMM d 'at' h:mm a")}`);
      setApptPickerOpen(false);
      setApptWhen("");
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["routes", "detail", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkOutcomeMut = useMutation({
    mutationFn: async (input: {
      stopIds: string[];
      status: RouteStopStatus;
      leadStatus: LeadStatus;
      eventType: "completed" | "no_answer" | "follow_up";
    }) => {
      // No bulk RPC yet — fan out sequentially so a single failure halts the
      // batch and the toast names the bad stop. Typical batch (25–50) finishes
      // well under a second; if this grows, add a bulk RPC parallel to
      // apply_route_stop_outcome.
      for (const stopId of input.stopIds) {
        await updateRouteStopStatus(stopId, input.status, {
          leadStatus: input.leadStatus,
          eventType: input.eventType,
        });
      }
      return input.stopIds.length;
    },
    onSuccess: (count) => {
      toast.success(`Updated ${count} stop${count === 1 ? "" : "s"}`);
      setSelectedStopIds(new Set());
      lastClickedStopIdRef.current = null;
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["routes", "detail", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ---------- Phase 2D: edit saved-route stops ----------
  // Removing a stop clears the polyline cache (see routes.ts) — invalidating
  // the directions query forces Detail to re-render with refreshed geometry.
  const removeStopMut = useMutation({
    mutationFn: (stopId: string) => removeRouteStop(stopId),
    onSuccess: () => {
      toast.success("Stop removed");
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["routes", "detail", id] });
      qc.invalidateQueries({ queryKey: ["directions"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const addStopMut = useMutation({
    mutationFn: (leadId: string) => appendRouteStop(id!, leadId),
    onSuccess: () => {
      toast.success("Stop added");
      setShowAddPicker(false);
      setAddPickerQuery("");
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["routes", "detail", id] });
      qc.invalidateQueries({ queryKey: ["directions"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["mappable-leads"] });
    },
    onError: (e: Error) => {
      // The double-booking trigger raises 23505 — surface the hint, not the
      // raw exception text. Other errors fall through to default formatting.
      const msg = e.message.includes("already on another active route")
        ? "That lead is already on another active route. Free it from the other route first."
        : e.message;
      toast.error(msg);
    },
  });

  // Lazy-load the add-stop candidate pool only when the picker is open.
  const addCandidatesQuery = useQuery({
    queryKey: ["mappable-leads", "for-route", id],
    queryFn: listMappableLeads,
    enabled: showAddPicker,
    staleTime: 30_000,
  });

  const route = routeQuery.data ?? null;
  const stops = useMemo(() => route?.stops ?? [], [route?.stops]);
  const total = stops.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStops = useMemo(
    () => stops.slice(page * pageSize, page * pageSize + pageSize),
    [stops, page, pageSize],
  );
  const pageStopIds = useMemo(() => pageStops.map((s) => s.id), [pageStops]);
  const pageSelState = pageSelectionState(pageStopIds, selectedStopIds);

  useEffect(() => {
    if (!stops.length) {
      setActiveStopId(null);
      return;
    }
    setActiveStopId((prev) => {
      if (prev && stops.some((s) => s.id === prev)) return prev;
      // Resume where the driver left off: mobile browsers evict backgrounded
      // tabs, so a reload must NOT reset to stop #1. Prefer the stop saved on
      // this device; if that stop was already worked, advance to the next
      // unworked stop after it. Otherwise fall back to the first unworked stop.
      const isOpen = (s: (typeof stops)[number]) => s.status === "pending" || s.status === "arrived";
      const saved = readSavedActiveStop(id);
      const savedIndex = saved ? stops.findIndex((s) => s.id === saved) : -1;
      if (savedIndex >= 0) {
        if (isOpen(stops[savedIndex])) return saved;
        const nextOpen = stops.slice(savedIndex + 1).find(isOpen);
        if (nextOpen) return nextOpen.id;
      }
      const firstOpen = stops.find(isOpen);
      return (firstOpen ?? stops[0]).id;
    });
  }, [route?.id, stops, id]);

  // Persist the driver's position per-route so app switches / tab kills
  // resume at the same stop. Cleared when the route reaches a terminal state.
  useEffect(() => {
    if (!id) return;
    if (route && (route.status === "completed" || route.status === "cancelled")) {
      clearSavedActiveStop(id);
      return;
    }
    if (activeStopId) writeSavedActiveStop(id, activeStopId);
  }, [id, activeStopId, route?.status, route]);

  useEffect(() => {
    if (!stops.length) return;
    setNoteDraftByStop((prev) => {
      const next = { ...prev };
      for (const s of stops) {
        next[s.id] = s.notes ?? "";
      }
      return next;
    });
  }, [stops]);

  // Keep the page in range when stops list shrinks (e.g. after a bulk update
  // re-loads with fewer pending rows) or when pageSize changes.
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages - 1));
  }, [totalPages]);

  // Flip pages so the activeStop is always visible — driver tapping Next/Prev
  // in the navigator shouldn't have to manually page.
  const activeStopIndex = useMemo(
    () => stops.findIndex((s) => s.id === activeStopId),
    [stops, activeStopId],
  );
  useEffect(() => {
    if (activeStopIndex < 0) return;
    const targetPage = Math.floor(activeStopIndex / pageSize);
    setPage((p) => (p === targetPage ? p : targetPage));
  }, [activeStopIndex, pageSize]);

  type RouteStopEntry = (typeof stops)[number];
  const activeStop = activeStopIndex >= 0 ? stops[activeStopIndex] : null;
  const previousStop = activeStopIndex > 0 ? stops[activeStopIndex - 1] : null;
  const nextStop = activeStopIndex >= 0 && activeStopIndex < stops.length - 1
    ? stops[activeStopIndex + 1]
    : null;

  // Resolve depot + stop geometry up front so the road-directions useQuery
  // below stays above any early `return`. React hooks rules require unchanging
  // call order across renders, so the loading/error/not-found bails come AFTER
  // all hook calls.
  const startDepot = route
    ? (depotsQuery.data ?? []).find((d) => d.id === route.depot_start_id)
    : undefined;

  const mapStops: MapStop[] = useMemo(() => {
    if (!route) return [];
    return route.stops
      .filter((s) => s.lead?.lat != null && s.lead?.lng != null)
      .map((s) => ({
        id: s.id,
        lat: s.lead!.lat as number,
        lng: s.lead!.lng as number,
        sequence: s.sequence,
        status: s.status,
        title: (s.lead?.full_name ?? `${s.lead?.first_name ?? ""} ${s.lead?.last_name ?? ""}`.trim()) || "Lead",
        subtitle: [s.lead?.address_street, s.lead?.address_city, s.lead?.address_state, s.lead?.address_zip].filter(Boolean).join(", "),
      }));
  }, [route]);

  // Resolve the end depot for the road-routing query. depot_end_id == null
  // means "end at last stop" (no return leg); same id as start = round-trip.
  const endDepot = route?.depot_end_id
    ? (depotsQuery.data ?? []).find((d) => d.id === route.depot_end_id) ?? null
    : null;
  const endPoint = !route || route.depot_end_id == null
    ? null  // open path or no-route-loaded
    : (endDepot && endDepot.lat != null && endDepot.lng != null
        ? { lat: endDepot.lat, lng: endDepot.lng }
        : null);

  // Real-road polyline for the saved route. Prefer the DB-cached encoded
  // polyline written by Plan.tsx at save time (migration 0009). Live
  // Directions call is the fallback for:
  //   - routes saved before the cache was wired (polyline_encoded = NULL)
  //   - routes whose stop list no longer matches what was cached (in practice
  //     this only happens if stops are inserted/removed post-save; today the
  //     planner doesn't expose that, so the cache effectively never staleens)
  const cachedPolyline = useMemo<Array<[number, number]> | null>(() => {
    if (!route?.polyline_encoded) return null;
    try {
      const decoded = decodePolyline(route.polyline_encoded);
      return decoded.length > 1 ? decoded : null;
    } catch {
      return null;
    }
  }, [route?.polyline_encoded]);

  const directionsEnabled =
    !!route &&
    !!startDepot &&
    startDepot.lat != null &&
    startDepot.lng != null &&
    mapStops.length > 0 &&
    mapStops.length <= DIRECTIONS_WAYPOINT_LIMIT &&
    !cachedPolyline; // skip the live call when we already have the geometry cached
  const directionsQuery = useQuery({
    enabled: directionsEnabled,
    queryKey: [
      "route-directions",
      "detail",
      route?.id ?? "",
      mapStops.map((s) => s.id).join(","),
    ],
    queryFn: () =>
      optimizeDriveRoute({
        origin: { lat: startDepot!.lat as number, lng: startDepot!.lng as number },
        destination: endPoint,
        waypoints: mapStops.map((s) => ({ id: s.id, lat: s.lat, lng: s.lng })),
        optimize: false,
      }),
    staleTime: 30 * 60_000,
    retry: 1,
  });

  if (routeQuery.isLoading) return <p className="text-sm text-slate-500">Loading route…</p>;
  if (routeQuery.error) return <p className="text-sm text-rose-700">Failed to load route.</p>;
  if (!route) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
        <p className="text-sm font-medium text-slate-700">Route not found</p>
        <Link to="/routes" className="mt-3 inline-flex items-center gap-1 text-sm text-brand-700 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to routes
        </Link>
      </div>
    );
  }

  const mapDepots: MapDepot[] = startDepot && startDepot.lat != null && startDepot.lng != null
    ? [{ id: startDepot.id, lat: startDepot.lat, lng: startDepot.lng, label: startDepot.name }]
    : [];

  // Polyline preference order, best → fallback:
  //   1. DB-cached encoded polyline decoded client-side (no Google call)
  //   2. Live Directions response from this page-load's fetch
  //   3. Straight-line crow-flies preview while either of the above loads
  const straightPolyline: Array<[number, number]> | undefined =
    startDepot && startDepot.lat != null && startDepot.lng != null && mapStops.length > 0
      ? [
          [startDepot.lat, startDepot.lng],
          ...mapStops.map((s) => [s.lat, s.lng] as [number, number]),
          ...(endPoint
            ? [[endPoint.lat, endPoint.lng] as [number, number]]
            : []),
        ]
      : undefined;
  const polyline: Array<[number, number]> | undefined =
    cachedPolyline ?? directionsQuery.data?.polyline ?? straightPolyline;

  // canManageStops gates every stop-mutating UI affordance (the navigator
  // outcome row, bulk-outcome toolbar, per-row outcome buttons, the notes
  // textarea + Save note button).
  //   - not in pool: pool routes need to be claimed first
  //   - admin OR assignee: only people with the route can mutate it
  //   - route status is non-terminal: marking outcomes on a `cancelled` or
  //     `completed` route would either un-cancel it (caught defensively in
  //     migration 0010) or revive a finished route. Hide the buttons.
  const canManageStops = !!me
    && !route.is_pool
    && (isAdmin || route.assigned_to === me.id)
    && route.status !== "cancelled"
    && route.status !== "completed";

  function normalizeLeadStatus(v: string | null | undefined): LeadStatus {
    if (v === "new" || v === "no_answer" || v === "scheduled" || v === "follow_up" || v === "closed" || v === "do_not_solicit") {
      return v;
    }
    return "new";
  }

  function jumpToStop(stopId: string, opts?: { scrollToNavigator?: boolean }) {
    setActiveStopId(stopId);
    // Booking panel is per-stop context — never carry it to another stop.
    setApptPickerOpen(false);
    // useEffect above flips the table page so the row stays visible without
    // scrolling. The client's name + info live in the navigator, so Next/Prev
    // (and row taps) bring the NAVIGATOR into view instead of flinging the
    // driver down to the table row at the bottom of the page.
    if (opts?.scrollToNavigator === false) return;
    requestAnimationFrame(() => {
      navigatorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function stopAddress(stop: RouteStopEntry): string {
    return [stop.lead?.address_street, stop.lead?.address_city, stop.lead?.address_state, stop.lead?.address_zip]
      .filter(Boolean)
      .join(", ");
  }

  function googleMapsUrl(stop: RouteStopEntry): string {
    const addr = stopAddress(stop);
    const coords = stop.lead?.lat != null && stop.lead?.lng != null ? `${stop.lead.lat},${stop.lead.lng}` : "";
    const destination = coords || addr;
    const qs = new URLSearchParams({ api: "1", destination });
    if (addr) qs.set("destination_place", addr);
    return `https://www.google.com/maps/dir/?${qs.toString()}`;
  }

  function onNavigatorTouchStart(e: React.TouchEvent<HTMLElement>) {
    swipeStartX.current = e.changedTouches[0]?.clientX ?? null;
  }

  function onNavigatorTouchEnd(e: React.TouchEvent<HTMLElement>) {
    if (swipeStartX.current == null) return;
    const endX = e.changedTouches[0]?.clientX ?? swipeStartX.current;
    const delta = endX - swipeStartX.current;
    swipeStartX.current = null;
    if (delta <= -48 && nextStop) jumpToStop(nextStop.id);
    if (delta >= 48 && previousStop) jumpToStop(previousStop.id);
  }

  function getStopNotes(stopId: string): string {
    return (noteDraftByStop[stopId] ?? "").trim();
  }

  function applyOutcome(stop: NonNullable<typeof activeStop>, stopStatus: RouteStopStatus, leadStatus: LeadStatus, eventType: "completed" | "no_answer" | "follow_up") {
    stopMut.mutate({
      stopId: stop.id,
      status: stopStatus,
      leadStatus,
      eventType,
      notes: getStopNotes(stop.id) || undefined,
    });
  }

  function handleStopSelectClick(stopId: string, shiftKey: boolean) {
    setSelectedStopIds((prev) => nextSelectionFromClick(stopId, shiftKey, pageStopIds, lastClickedStopIdRef.current, prev));
    lastClickedStopIdRef.current = stopId;
  }

  function handlePageToggle() {
    setSelectedStopIds((prev) => togglePageSelection(pageStopIds, prev));
    lastClickedStopIdRef.current = null;
  }

  function clearSelection() {
    setSelectedStopIds(new Set());
    lastClickedStopIdRef.current = null;
  }

  function runBulkOutcome(stopStatus: RouteStopStatus, leadStatus: LeadStatus, eventType: "completed" | "no_answer" | "follow_up") {
    if (!selectedStopIds.size || bulkOutcomeMut.isPending) return;
    bulkOutcomeMut.mutate({
      stopIds: [...selectedStopIds],
      status: stopStatus,
      leadStatus,
      eventType,
    });
  }

  const customFieldEntries = activeStop?.lead?.custom_fields
    ? Object.entries(activeStop.lead.custom_fields).filter(([, v]) => v != null && v !== "")
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/routes" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
            <ArrowLeft className="h-3 w-3" /> All routes
          </Link>
          <h2 className="mt-1 text-xl font-semibold text-slate-900">{route.name}</h2>
          <div className="mt-1 flex items-center gap-3 text-xs text-slate-600">
            <span>{format(parseISO(route.route_date), "EEE, MMM d, yyyy")}</span>
            <span>·</span>
            <span>
              {isAdmin && route.status !== "completed" && route.status !== "cancelled" ? (
                <select
                  value={route.is_pool ? "pool" : route.assigned_to ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v || v === (route.is_pool ? "pool" : route.assigned_to ?? "")) return;
                    const label = v === "pool"
                      ? "send this route to the team pool"
                      : `reassign this route to ${(membersQuery.data ?? []).find((m) => m.id === v)?.fullName ?? "this member"}`;
                    if (window.confirm(`Really ${label}?`)) reassignMut.mutate(v as string | "pool");
                    else e.currentTarget.value = route.is_pool ? "pool" : route.assigned_to ?? "";
                  }}
                  disabled={reassignMut.isPending}
                  className="rounded-md border-slate-300 py-0.5 pl-1.5 pr-6 text-xs shadow-sm focus:border-brand-500 focus:ring-brand-500 disabled:opacity-50"
                  title="Reassign this route"
                >
                  {!route.is_pool && !route.assigned_to && <option value="">unassigned</option>}
                  {(membersQuery.data ?? []).filter((m) => m.isActive).map((m) => (
                    <option key={m.id} value={m.id}>{m.fullName}</option>
                  ))}
                  <option value="pool">Team pool</option>
                </select>
              ) : route.is_pool ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase text-amber-700">
                  Team pool
                </span>
              ) : (
                route.assigned_to_name ?? "unassigned"
              )}
            </span>
            <span>·</span>
            <span>{route.stops.length} stops</span>
            {route.total_distance_m != null && (
              <>
                <span>·</span>
                <span>{formatMiles(route.total_distance_m)}</span>
              </>
            )}
            {(() => {
              // Cached drive time wins; fall back to the live query.
              const seconds = route.total_duration_s ?? directionsQuery.data?.total_duration_s ?? null;
              if (seconds == null) return null;
              return (
                <>
                  <span>·</span>
                  <span>{formatDuration(seconds)} drive</span>
                </>
              );
            })()}
            <span>·</span>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${STATUS_STYLES[route.status]}`}>
              {route.status.replace("_", " ")}
            </span>
          </div>
        </div>
        <div className="inline-flex items-center gap-2">
          {/* Cancel: any admin OR the assignee can cancel a non-terminal route.
              Soft-stops the route — leads return to the pool via the 0008
              trigger — while preserving the visit_events audit trail for the
              stops that were already worked. Delete is admin-only and hard. */}
          {(isAdmin || (me && route.assigned_to === me.id))
            && route.status !== "completed"
            && route.status !== "cancelled" && (
            <button
              type="button"
              onClick={() => {
                if (confirm(`Cancel route "${route.name}"? Any pending/arrived stops will release their leads back to the pool. The route + visit history stays in place.`)) {
                  cancelMut.mutate();
                }
              }}
              disabled={cancelMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 shadow-sm hover:bg-amber-50 disabled:opacity-40"
            >
              {cancelMut.isPending ? "Cancelling…" : "Cancel route"}
            </button>
          )}
          {isFullAdmin && (
            <button
              type="button"
              onClick={() => {
                if (confirm(`Delete route "${route.name}"? This is permanent — visit history will be lost.`)) deleteMut.mutate();
              }}
              disabled={deleteMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 shadow-sm hover:bg-rose-50 disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </div>

      {route.is_pool && (
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-amber-900">This route is in the team pool.</p>
            <p className="text-xs text-amber-700">First driver to claim it owns it. After claiming, it disappears from other drivers&apos; queues.</p>
          </div>
          <button
            type="button"
            onClick={() => claimMut.mutate()}
            disabled={claimMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-50"
          >
            <Hand className="h-4 w-4" /> {claimMut.isPending ? "Claiming…" : "Claim route"}
          </button>
        </div>
      )}

      <LeafletMap depots={mapDepots} stops={mapStops} polyline={polyline} height={500} />

      {(() => {
        // The maps export is regenerated from CURRENT route state every render
        // — completed / skipped / no-answer stops drop out, leaving only the
        // pending + arrived chain. That's our "live update" handle: there's no
        // way to push live updates *into* an already-open Maps app via URL
        // deep link (Apple and Google both treat the URL as one-shot), but the
        // driver can tap Open in Maps after each outcome and get a fresh URL
        // with only the remaining work.
        if (!startDepot || startDepot.lat == null || startDepot.lng == null) return null;
        if (!route) return null;
        const activeStops = route.stops
          .filter((s) =>
            s.lead?.lat != null &&
            s.lead?.lng != null &&
            (s.status === "pending" || s.status === "arrived"),
          )
          .map((s) => ({
            lat: s.lead!.lat as number,
            lng: s.lead!.lng as number,
            label: (s.lead?.full_name ?? `${s.lead?.first_name ?? ""} ${s.lead?.last_name ?? ""}`.trim()) || "Lead",
            address: [s.lead?.address_street, s.lead?.address_city, s.lead?.address_state, s.lead?.address_zip].filter(Boolean).join(", "),
          }));
        if (activeStops.length === 0) return null;
        return (
          <RouteMapsExport
            origin={{ lat: startDepot.lat, lng: startDepot.lng }}
            stops={activeStops}
            destination={endPoint}
            totalStopCount={route.stops.length}
          />
        );
      })()}

      {canManageStops && activeStop && (
        <section
          ref={navigatorRef}
          className="scroll-mt-4 space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
          onTouchStart={onNavigatorTouchStart}
          onTouchEnd={onNavigatorTouchEnd}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Stop navigator</p>
              <p className="text-sm font-medium text-slate-900">
                Stop {activeStop.sequence} of {route.stops.length}: {(activeStop.lead?.full_name ?? `${activeStop.lead?.first_name ?? ""} ${activeStop.lead?.last_name ?? ""}`.trim()) || "Unknown"}
              </p>
              <p className="text-xs text-slate-500">Swipe left/right on mobile to move between stops.</p>
            </div>
            <div className="inline-flex items-center gap-2">
              {/* Per-row "Navigate" link in the Stops table handles single-stop
                  Maps export; the route-level "Open in Maps" panel above
                  handles the full chain. Navigator keeps just the step
                  controls for the active stop. */}
              <button
                type="button"
                onClick={() => previousStop && jumpToStop(previousStop.id)}
                disabled={!previousStop}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Previous stop
              </button>
              <button
                type="button"
                onClick={() => nextStop && jumpToStop(nextStop.id)}
                disabled={!nextStop}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                Next stop <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 rounded-md border border-slate-100 bg-slate-50/60 p-3 text-xs sm:grid-cols-2 lg:grid-cols-3">
            {/* Address + Phone + DOB always render (even if empty) so the grid
                columns stay aligned — these are the always-load-bearing rows.
                Alt phone, email, source, custom_fields only render when the
                lead actually has a value; otherwise the "—" rows just clutter. */}
            <LeadDetailRow label="Address" value={stopAddress(activeStop) || activeStop.lead?.address_combined || "—"} />
            <LeadDetailRow
              label="Phone"
              value={activeStop.lead?.phone ? (formatPhoneDisplay(activeStop.lead.phone) || activeStop.lead.phone) : "—"}
              href={toTelHref(activeStop.lead?.phone)}
            />
            <LeadDetailRow label="DOB" value={formatDob(activeStop.lead?.dob)} />
            {activeStop.lead?.phone_alt && (
              <LeadDetailRow
                label="Alt phone"
                value={formatPhoneDisplay(activeStop.lead.phone_alt) || activeStop.lead.phone_alt}
                href={toTelHref(activeStop.lead.phone_alt)}
              />
            )}
            {activeStop.lead?.email && (
              <LeadDetailRow
                label="Email"
                value={activeStop.lead.email}
                href={toMailtoHref(activeStop.lead.email)}
              />
            )}
            {activeStop.lead?.source && (
              <LeadDetailRow label="Source" value={activeStop.lead.source} />
            )}
            {customFieldEntries.map(([key, value]) => (
              <LeadDetailRow
                key={key}
                label={humanizeCustomFieldKey(key)}
                value={formatCustomFieldValue(value)}
              />
            ))}
          </dl>

          <textarea
            value={noteDraftByStop[activeStop.id] ?? ""}
            onChange={(e) => setNoteDraftByStop((prev) => ({ ...prev, [activeStop.id]: e.target.value }))}
            rows={3}
            placeholder="Add stop notes (door response, follow-up context, objections, timing, etc.)"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-brand-500 focus:ring-brand-500"
          />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => applyOutcome(activeStop, "completed", "closed", "completed")}
                disabled={stopMut.isPending}
                className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                title="Sale closed — sets lead status to Sold"
              >
                Sold
              </button>
              <button
                type="button"
                onClick={() => applyOutcome(activeStop, "no_answer", "no_answer", "no_answer")}
                disabled={stopMut.isPending}
                className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-40"
              >
                No answer
              </button>
              <button
                type="button"
                onClick={() => applyOutcome(activeStop, "skipped", "follow_up", "follow_up")}
                disabled={stopMut.isPending}
                className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-40"
              >
                Follow up
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!apptPickerOpen) {
                    // Default to tomorrow 10:00 local — the most common booking.
                    const d = new Date();
                    d.setDate(d.getDate() + 1);
                    d.setHours(10, 0, 0, 0);
                    setApptWhen(format(d, "yyyy-MM-dd'T'HH:mm"));
                  }
                  setApptPickerOpen((v) => !v);
                }}
                disabled={stopMut.isPending || apptMut.isPending}
                className="rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-700 hover:bg-sky-100 disabled:opacity-40"
                title="Book a date/time with this lead — completes the stop and marks the lead Scheduled"
              >
                Set appointment
              </button>
            </div>

            <button
              type="button"
              onClick={() => stopMut.mutate({
                stopId: activeStop.id,
                status: activeStop.status,
                leadStatus: normalizeLeadStatus(activeStop.lead?.status),
                eventType: "note",
                notes: getStopNotes(activeStop.id) || " ",
              })}
              disabled={stopMut.isPending}
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              Save note
            </button>
          </div>

          {apptPickerOpen && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-sky-50/60 p-3">
              <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
                Appointment time
                <input
                  type="datetime-local"
                  value={apptWhen}
                  onChange={(e) => setApptWhen(e.target.value)}
                  className="rounded-md border-slate-300 px-2 py-1.5 text-xs shadow-sm focus:border-brand-500 focus:ring-brand-500"
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  const parsed = apptWhen ? new Date(apptWhen) : null;
                  if (!parsed || Number.isNaN(parsed.getTime())) {
                    toast.error("Pick a valid date and time first");
                    return;
                  }
                  apptMut.mutate({
                    stopId: activeStop.id,
                    scheduledAt: parsed.toISOString(),
                    notes: getStopNotes(activeStop.id) || undefined,
                  });
                }}
                disabled={apptMut.isPending}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-40"
              >
                {apptMut.isPending ? "Booking…" : "Book appointment"}
              </button>
              <button
                type="button"
                onClick={() => setApptPickerOpen(false)}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <p className="w-full text-[11px] text-slate-500">
                Booking completes this stop, sets the lead to Scheduled, and adds the appointment to the dashboard.
              </p>
            </div>
          )}
        </section>
      )}

      {canManageStops && selectedStopIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm">
          <span className="text-slate-700">
            <span className="font-semibold">{selectedStopIds.size}</span> stop{selectedStopIds.size === 1 ? "" : "s"} selected
            <button
              type="button"
              onClick={clearSelection}
              className="ml-3 text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
            >
              Clear
            </button>
          </span>
          <div className="inline-flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Bulk outcome</span>
            <button
              type="button"
              onClick={() => runBulkOutcome("completed", "closed", "completed")}
              disabled={bulkOutcomeMut.isPending}
              className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
              title="Sale closed for every selected stop"
            >
              Sold
            </button>
            <button
              type="button"
              onClick={() => runBulkOutcome("no_answer", "no_answer", "no_answer")}
              disabled={bulkOutcomeMut.isPending}
              className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-40"
            >
              No answer
            </button>
            <button
              type="button"
              onClick={() => runBulkOutcome("skipped", "follow_up", "follow_up")}
              disabled={bulkOutcomeMut.isPending}
              className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-40"
            >
              Follow up
            </button>
          </div>
        </div>
      )}

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Stops</h3>
          {canManageStops && (
            <button
              type="button"
              onClick={() => setShowAddPicker((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-700 hover:bg-sky-100"
            >
              <Plus className="h-3 w-3" />
              {showAddPicker ? "Close" : "Add stop"}
            </button>
          )}
        </header>
        {canManageStops && showAddPicker && (
          <AddStopPicker
            candidates={addCandidatesQuery.data ?? []}
            loading={addCandidatesQuery.isLoading}
            existingLeadIds={new Set(stops.map((s) => s.lead_id).filter((x): x is string => !!x))}
            query={addPickerQuery}
            setQuery={setAddPickerQuery}
            onPick={(leadId) => addStopMut.mutate(leadId)}
            disabled={addStopMut.isPending}
          />
        )}
        <div className="w-full overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              {canManageStops && (
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={pageSelState === "all"}
                    ref={(el) => {
                      if (el) el.indeterminate = pageSelState === "some";
                    }}
                    onChange={handlePageToggle}
                    aria-label={pageSelState === "none" ? "Select all stops on this page" : "Clear selection on this page"}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                </th>
              )}
              <th className="w-10 px-3 py-2">#</th>
              <th className="px-3 py-2">Lead</th>
              <th className="px-3 py-2">Leg</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Outcome</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageStops.map((s) => {
              return (
                <tr
                  key={s.id}
                  className={[
                    "hover:bg-slate-50",
                    activeStopId === s.id ? "bg-sky-50/60" : "",
                    selectedStopIds.has(s.id) ? "bg-sky-50/80" : "",
                  ].join(" ")}
                >
                  {canManageStops && (
                    <td className="w-10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedStopIds.has(s.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStopSelectClick(s.id, e.shiftKey);
                        }}
                        onChange={() => { /* handled in onClick to capture shiftKey */ }}
                        aria-label={`Select stop ${s.sequence}`}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                    </td>
                  )}
                  <td className="w-10 px-3 py-2 text-xs font-bold text-sky-700">{s.sequence}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => jumpToStop(s.id)}
                      className="text-left"
                    >
                      <div className="text-sm font-medium text-slate-900 hover:underline">
                        {(s.lead?.full_name ?? `${s.lead?.first_name ?? ""} ${s.lead?.last_name ?? ""}`.trim()) || "Unknown"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {[s.lead?.address_street, s.lead?.address_city, s.lead?.address_state, s.lead?.address_zip].filter(Boolean).join(", ")}
                      </div>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {s.distance_from_prev_m != null ? formatMiles(s.distance_from_prev_m) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {s.lead?.phone ? (
                      <a
                        href={toTelHref(s.lead.phone) ?? "#"}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sky-700 hover:underline"
                      >
                        {formatPhoneDisplay(s.lead.phone) || s.lead.phone}
                      </a>
                    ) : ""}
                  </td>
                  <td className="px-3 py-2">
                    {s.lead?.status ? (
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${LEAD_STATUS_STYLES[s.lead.status as LeadStatus] ?? STOP_STATUS_STYLES[s.status]}`}
                      >
                        {s.lead.status.replace("_", " ")}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">unknown</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex flex-wrap justify-end gap-1.5">
                      <a
                        href={googleMapsUrl(s)}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-700 hover:bg-sky-100"
                      >
                        Navigate
                      </a>
                      {canManageStops && (s.status === "pending" || s.status === "arrived") && (
                        <button
                          type="button"
                          onClick={() => stopMut.mutate({ stopId: s.id, status: "completed", leadStatus: "closed", eventType: "completed", notes: getStopNotes(s.id) || undefined })}
                          disabled={stopMut.isPending}
                          className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                          title="Sale closed"
                        >
                          Sold
                        </button>
                      )}
                      {canManageStops && (s.status === "pending" || s.status === "arrived") && (
                        <button
                          type="button"
                          onClick={() => stopMut.mutate({ stopId: s.id, status: "no_answer", leadStatus: "no_answer", eventType: "no_answer", notes: getStopNotes(s.id) || undefined })}
                          disabled={stopMut.isPending}
                          className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-40"
                        >
                          No answer
                        </button>
                      )}
                      {canManageStops && (s.status === "pending" || s.status === "arrived") && (
                        <button
                          type="button"
                          onClick={() => stopMut.mutate({ stopId: s.id, status: "skipped", leadStatus: "follow_up", eventType: "follow_up", notes: getStopNotes(s.id) || undefined })}
                          disabled={stopMut.isPending}
                          className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-40"
                        >
                          Follow up
                        </button>
                      )}
                      {canManageStops && (s.status === "pending" || s.status === "arrived") && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Remove stop #${s.sequence} from this route? The lead returns to the routable pool.`)) {
                              removeStopMut.mutate(s.id);
                            }
                          }}
                          disabled={removeStopMut.isPending}
                          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                          title="Remove this stop from the route"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600">
            <span>
              {(page * pageSize + 1).toLocaleString()}–
              {Math.min((page + 1) * pageSize, total).toLocaleString()} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-slate-500">
                Per page
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value) as PageSize); setPage(0); }}
                  className="rounded-md border-slate-300 py-1 pl-2 pr-7 text-xs shadow-sm focus:border-brand-500 focus:ring-brand-500"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-md border border-slate-300 bg-white px-3 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded-md border border-slate-300 bg-white px-3 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function RouteMapsExport({
  origin,
  stops,
  destination,
  totalStopCount,
}: {
  origin: { lat: number; lng: number };
  stops: Array<{ lat: number; lng: number; label?: string; address?: string }>;
  destination: { lat: number; lng: number } | null;
  /** Total stops on the route (pending + already-closed). `stops.length`
   *  reflects only the still-active subset that the maps URL will contain. */
  totalStopCount: number;
}) {
  const googleUrl = buildGoogleMapsRouteUrl(origin, stops, destination);
  const appleUrl = buildAppleMapsRouteUrl(origin, stops, destination);
  const onApple = preferAppleMaps();
  const [copied, setCopied] = useState(false);

  async function onCopyAddresses() {
    const text = buildRouteAddressList(origin, stops, destination);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Addresses copied — paste into any maps app");
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't copy to clipboard");
    }
  }

  // Both deep-link buttons render on every platform. On iOS / iPadOS / macOS
  // the Apple link Universal-Links into the native Apple Maps app where the
  // `+to:` chain parses correctly. On Windows / Linux / Android the link
  // opens maps.apple.com web — which historically renders only the first
  // stop because Apple's web preview hasn't shipped multi-stop. That's an
  // Apple-side gap, not something a different URL format fixes. The Copy
  // addresses button is the always-works backstop for any platform that
  // can't honor the Apple deep link — pastes into any maps/notes app.
  return (
    <section className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Open the whole route in maps
        </p>
        <p className="mt-0.5 text-[11px] text-slate-500">
          <span className="font-semibold text-slate-700">
            {stops.length} of {totalStopCount} stops
          </span>{" "}
          still active — closed / skipped / no-answer stops drop out
          automatically. Tap again after any outcome to regenerate.
          {!onApple && (
            <>
              {" "}
              <span className="text-slate-400">
                Apple Maps web preview may only show the first stop — use
                Copy addresses for the full route on non-Apple devices.
              </span>
            </>
          )}
        </p>
      </div>
      <div className="inline-flex flex-wrap items-center gap-2">
        <a
          href={googleUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700"
        >
          Open in Google Maps
        </a>
        <a
          href={appleUrl}
          target="_blank"
          rel="noreferrer"
          className={onApple
            ? "inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700"
            : "inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          }
        >
          Open in Apple Maps
        </a>
        <button
          type="button"
          onClick={onCopyAddresses}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
        >
          {copied ? "Copied ✓" : "Copy addresses"}
        </button>
      </div>
    </section>
  );
}

function LeadDetailRow({ label, value, href }: { label: string; value: string; href?: string | null }) {
  return (
    <div className="flex flex-wrap items-baseline gap-1.5">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-xs text-slate-700">
        {href ? (
          <a href={href} className="text-sky-700 hover:underline">{value}</a>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

// ---------- Phase 2D: add-stop picker ---------------------------------------

interface AddStopPickerProps {
  candidates: MappableLead[];
  loading: boolean;
  /** Lead ids already on this route — used to filter out duplicates that the
   *  blocked-lead RPC would also catch, but filtering client-side avoids the
   *  driver clicking and getting a 23505 toast back. */
  existingLeadIds: Set<string>;
  query: string;
  setQuery: (q: string) => void;
  onPick: (leadId: string) => void;
  disabled: boolean;
}

function AddStopPicker({ candidates, loading, existingLeadIds, query, setQuery, onPick, disabled }: AddStopPickerProps) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = candidates.filter((c) => !existingLeadIds.has(c.id));
    if (!q) return pool.slice(0, 50);
    return pool
      .filter((c) => {
        const name = (c.full_name ?? `${c.first_name ?? ""} ${c.last_name ?? ""}`).toLowerCase();
        const addr = [c.address_street, c.address_city, c.address_state, c.address_zip].filter(Boolean).join(" ").toLowerCase();
        const phone = (c.phone ?? "").toLowerCase();
        return name.includes(q) || addr.includes(q) || phone.includes(q);
      })
      .slice(0, 50);
  }, [candidates, existingLeadIds, query]);

  return (
    <div className="border-b border-slate-200 bg-sky-50/50 px-4 py-3">
      <p className="mb-2 text-[11px] text-slate-600">
        Pick a lead to append as the last stop. The route geometry and timing recalculate on next open.
      </p>
      <input
        type="text"
        placeholder="Search by name, address, or phone…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="mb-2 block w-full rounded-md border-slate-300 px-3 py-1.5 text-xs shadow-sm focus:border-sky-500 focus:ring-sky-500"
        autoFocus
      />
      {loading && <div className="py-2 text-xs text-slate-500">Loading eligible leads…</div>}
      {!loading && filtered.length === 0 && (
        <div className="py-2 text-xs text-slate-500">
          {candidates.length === 0
            ? "No leads available to add. Import leads or free them from other active routes first."
            : query
              ? "No matches. Try a different name or address."
              : "Every eligible lead is already on this route."}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 bg-white">
          {filtered.map((c) => {
            const name = (c.full_name ?? `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim()) || "Lead";
            const addr = [c.address_street, c.address_city, c.address_state, c.address_zip].filter(Boolean).join(", ");
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onPick(c.id)}
                  disabled={disabled}
                  className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-slate-900">{name}</div>
                    <div className="truncate text-[11px] text-slate-500">{addr || "—"}</div>
                  </div>
                  <span className="shrink-0 rounded-md border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">
                    Add
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
